import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listDoctors,
  deleteDoctor,
  Doctor,
  DoctorListParams,
  DoctorStatus,
} from "../../services/superAdmin.service";
import CustomSelect from "../../components/common/CustomSelect";
import Modal from "../../components/common/Modal";
import {
  Stethoscope,
  Search,
  ChevronLeft,
  ChevronRight,
  Mail,
  Building2,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Phone,
  Award,
  Briefcase,
  GraduationCap,
  FileText,
  ClipboardCheck,
} from "lucide-react";

const STATUS_OPTIONS: { label: string; value: DoctorStatus | "" }[] = [
  { label: "All Status", value: "" },
  { label: "Active", value: "active" },
  { label: "In Progress", value: "in_progress" },
  { label: "Inactive", value: "inactive" },
];

const getDoctorStatusFromSearch = (search: string): DoctorStatus | "" => {
  const status = new URLSearchParams(search).get("status");
  return status === "active" || status === "in_progress" || status === "inactive"
    ? status
    : "";
};

const DoctorList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<DoctorStatus | "">(
    () => getDoctorStatusFromSearch(location.search)
  );
  const [page, setPage] = useState(1);
  const limit = 10;

  // Modals
  const [viewDoctor, setViewDoctor] = useState<Doctor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Doctor | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setStatusFilter(getDoctorStatusFromSearch(location.search));
  }, [location.search]);

  const fetchDoctors = useCallback(async (isSearch = false) => {
    if (isSearch) setSearching(true);
    else setLoading(true);
    try {
      const params: DoctorListParams = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      const result = await listDoctors(params);
      setDoctors(result.doctors);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      toast.error("Failed to load doctors");
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoctor(deleteTarget.id);
      toast.success(`Dr. ${deleteTarget.name} deleted successfully`);
      setDeleteTarget(null);
      fetchDoctors();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete doctor");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by doctor name, email, or hospital..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <CustomSelect
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val as DoctorStatus | "");
              setPage(1);
            }}
            options={STATUS_OPTIONS}
            variant="filter"
            placeholder="All Status"
            className="h-10"
          />
          <button
            onClick={() => navigate("/doctors/new")}
            className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 h-10 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            Add Doctor
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
        {searching && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary-100 overflow-hidden z-10">
            <div className="h-full bg-primary-500 animate-loading-bar"></div>
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading doctors...</p>
          </div>
        ) : doctors.length === 0 ? (
          <div className="p-12 text-center">
            <Stethoscope size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No doctors found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search || statusFilter ? "Try adjusting your filters" : "No doctors are registered yet"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Doctor
                    </th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Hospital
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {doctors.map((d) => (
                    <tr key={`${d.hospitalId}-${d.id}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                            <Stethoscope size={18} />
                          </div>
                          <p className="text-sm font-semibold text-gray-900">{d.name || "—"}</p>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4">
                        <span className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Mail size={13} className="text-gray-400" />
                          {d.email || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Building2 size={13} className="text-gray-400" />
                          {d.hospitalName || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={d.status} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setViewDoctor(d)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                            title="View doctor"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/doctors/${d.id}/edit`)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-all"
                            title="Edit doctor"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(d)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-danger-500 hover:bg-danger-50 hover:text-danger-700 transition-all"
                            title="Delete doctor"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700 font-medium px-2">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Doctor Modal */}
      {viewDoctor && <DoctorViewModal doctor={viewDoctor} onClose={() => setViewDoctor(null)} />}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <Modal isOpen title="Delete Doctor" onClose={() => setDeleteTarget(null)}>
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>Dr. {deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* --- View Doctor Modal (full details) --- */
const DoctorViewModal = ({ doctor, onClose }: { doctor: Doctor; onClose: () => void }) => (
  <Modal isOpen title="Doctor Details" onClose={onClose}>
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
          <Stethoscope size={24} />
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-900">
            {doctor.firstName || doctor.lastName
              ? `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim()
              : doctor.name || "—"}
          </p>
          <p className="text-sm text-gray-500">{doctor.specialization || "—"}</p>
        </div>
      </div>
      <hr className="border-gray-100" />

      {/* Personal Information */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Personal Information</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <ViewField icon={<Mail size={13} />} label="Email" value={doctor.email} />
          <ViewField icon={<Phone size={13} />} label="Phone Number" value={doctor.phoneNumber} />
        </div>
      </div>

      {/* Professional Details */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Professional Details</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <ViewField icon={<Award size={13} />} label="Specialization" value={doctor.specialization} />
          <ViewField icon={<Briefcase size={13} />} label="Department" value={doctor.department} />
          <ViewField icon={<GraduationCap size={13} />} label="Qualification" value={doctor.qualification} />
          <ViewField icon={<FileText size={13} />} label="Med Registration No." value={doctor.medicalRegistrationNumber} />
          <ViewField icon={<FileText size={13} />} label="License Number" value={doctor.licenseNumber} />
          <ViewField icon={<Briefcase size={13} />} label="Years of Experience" value={doctor.yearsOfExperience != null ? `${doctor.yearsOfExperience} Years` : undefined} />
        </div>
      </div>

      {/* Assignment */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Assignment</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <ViewField icon={<Building2 size={13} />} label="Hospital" value={doctor.hospitalName} />
          <ViewField icon={<ClipboardCheck size={13} />} label="Onboarding Status" value={doctor.onboardingStatus} />
          <div>
            <p className="text-gray-500 mb-0.5 text-xs">Status</p>
            <StatusBadge status={doctor.status} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Subscription</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <ViewField
            icon={<Award size={13} />}
            label="Plan Name"
            value={doctor.subscriptionPlanName || (doctor.subscriptionPlanId ? "Assigned plan" : undefined)}
          />
          <ViewField
            icon={<FileText size={13} />}
            label="Plan Price"
            value={
              doctor.subscriptionPlanPrice != null && doctor.subscriptionPlanPrice !== ""
                ? `${doctor.subscriptionPlanPrice}${doctor.subscriptionPlanCurrency ? ` ${doctor.subscriptionPlanCurrency}` : ""}`
                : undefined
            }
          />
        </div>
      </div>
    </div>
  </Modal>
);

const ViewField = ({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | number | null }) => (
  <div>
    <p className="text-gray-500 mb-0.5 text-xs">{label}</p>
    <p className="font-medium text-gray-900 flex items-center gap-1.5">
      <span className="text-gray-400">{icon}</span>
      {value != null && value !== "" ? String(value) : "—"}
    </p>
  </div>
);

const StatusBadge = ({ status }: { status: DoctorStatus }) => {
  let classes = "bg-gray-100 text-gray-600";
  let label = status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  switch (status) {
    case "active":
      classes = "bg-green-50 text-green-700 border border-green-100";
      break;
    case "in_progress":
      classes = "bg-blue-50 text-blue-700 border border-blue-100";
      break;
    case "inactive":
      classes = "bg-gray-50 text-gray-500 border border-gray-100";
      break;
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
};

export default DoctorList;
